import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../config/api';
import { 
  masterPanel, 
  masterFormContainer, 
  masterFormGrid3, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect, 
  masterAccentButton, 
  masterPrimaryButton,
  masterSectionTitle, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableRow, 
  masterTableData, 
  masterTableID, 
  masterActiveColor, 
  masterInactiveColor, 
  masterTableWrap,
  masterEditButton,
  masterCancelButton,
  masterTableHeaderCell,
  masterTableHeaderCellCenter,
  masterTableStatusCell,
  masterTableActionCell,
  masterFormActions
} from '../Pages/MasterCssClass';

const ClinetMaster = () => {
  const [clients, setClients] = useState([]);
  const [showList, setShowList] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [formData, setFormData] = useState({
    clientName: '',
    clientCode: '',
    address: '',
    email: '',
    gstin: '',
    mobileNumber: '',
    status: 'Active'
  });

  const fetchClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch (err) {
      console.error('Error fetching clients', err);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate mobile number: exactly 10 digits
    const mobileRegex = /^\d{10}$/;
    if (formData.mobileNumber && !mobileRegex.test(formData.mobileNumber)) {
      toast.error('Mobile number must be exactly 10 digits');
      return;
    }

    try {
      if (editId) {
        await api.put(`/clients/${editId}`, formData);
        toast.success('Client updated successfully!');
      } else {
        await api.post('/clients', formData);
        toast.success('Client saved successfully with auto-generated ID!');
      }

      setFormData({ clientName: '', clientCode: '', address: '', email: '', gstin: '', mobileNumber: '', status: 'Active' });
      setEditId(null);
      fetchClients();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      toast.error(`Error saving client record: ${errorMsg}`);
    }
  };

  const handleEdit = (client) => {
    setFormData({
      clientName: client.clientName || '',
      clientCode: client.clientCode || '',
      address: client.address || '',
      email: client.email || '',
      gstin: client.gstin || '',
      mobileNumber: client.mobileNumber || '',
      status: client.status || 'Active'
    });
    setEditId(client._id);
    setShowList(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (err) {
      toast.error('Failed to delete client record');
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-end px-1">
        <button 
          onClick={() => setShowList(!showList)} 
          className={masterPrimaryButton}
        >
          {showList ? 'Add New Client (Form)' : `View List (${clients.length})`}
        </button>
      </div>

      {!showList ? (
        <div className={masterPanel}>
          <div className={masterFormContainer}>
            <h2 className={masterSectionTitle}>
              {editId ? 'Edit Client Master' : 'Client Master Entry (Auto ID Generation)'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-2">
              <div className={masterFormGrid3}>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Client Name</label>
                  <input 
                    type="text" 
                    value={formData.clientName} 
                    onChange={(e) => setFormData({...formData, clientName: e.target.value})} 
                    className={masterInput} 
                  />
                </div>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Client Code</label>
                  <input 
                    type="text" 
                    value={formData.clientCode} 
                    onChange={(e) => setFormData({...formData, clientCode: e.target.value})} 
                    className={masterInput} 
                  />
                </div>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Email</label>
                  <input 
                    type="email" 
                    value={formData.email} 
                    onChange={(e) => setFormData({...formData, email: e.target.value})} 
                    className={masterInput} 
                  />
                </div>
              </div>

              <div className={masterFormGrid3}>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Mobile Number</label>
                  <input 
                    type="text" 
                    maxLength="10"
                    value={formData.mobileNumber} 
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({...formData, mobileNumber: val});
                    }} 
                    className={masterInput} 
                  />
                </div>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>GSTIN No</label>
                  <input 
                    type="text" 
                    value={formData.gstin} 
                    onChange={(e) => setFormData({...formData, gstin: e.target.value})} 
                    className={masterInput} 
                  />
                </div>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select 
                    value={formData.status} 
                    onChange={(e) => setFormData({...formData, status: e.target.value})} 
                    className={masterSelect}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className={masterFormGridRow}>
                <label className={masterInputLabel}>Address</label>
                <input 
                  type="text" 
                  value={formData.address} 
                  onChange={(e) => setFormData({...formData, address: e.target.value})} 
                  className={masterInput} 
                />
              </div>

              <div className={masterFormActions}>
                {editId && (
                  <button 
                    type="button" 
                    onClick={() => { setEditId(null); setFormData({ clientName: '', clientCode: '', address: '', email: '', gstin: '', mobileNumber: '', status: 'Active' }); }}
                    className={masterCancelButton}
                  >
                    Cancel
                  </button>
                )}
                <button type="submit" className={masterAccentButton}>
                  {editId ? 'Update Client' : 'Save Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className={masterPanel}>
          <div className={masterTableWrap}>
            <table className={masterTable}>
              <thead>
                <tr className={masterStickyHeadRow}>
                  <th className={masterTableHeaderCell}>Client ID</th>
                  <th className={masterTableHeaderCell}>Client Name</th>
                  <th className={masterTableHeaderCell}>Client Code</th>
                  <th className={masterTableHeaderCell}>Address</th>
                  <th className={masterTableHeaderCell}>Email</th>
                  <th className={masterTableHeaderCell}>Mobile</th>
                  <th className={masterTableHeaderCell}>GSTIN</th>
                  <th className={masterTableHeaderCellCenter}>Status</th>
                  <th className={masterTableHeaderCellCenter}>Action</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c._id} className={masterTableRow}>
                    <td className={masterTableID}>{c.clientId}</td>
                    <td className={masterTableData}>{c.clientName || 'N/A'}</td>
                    <td className={masterTableData}>{c.clientCode || 'N/A'}</td>
                    <td className={masterTableData}>{c.address || 'N/A'}</td>
                    <td className={masterTableData}>{c.email || 'N/A'}</td>
                    <td className={masterTableData}>{c.mobileNumber || 'N/A'}</td>
                    <td className={masterTableData}>{c.gstin || 'N/A'}</td>
                    <td className={masterTableStatusCell}>
                      <span className={c.status === 'Active' ? masterActiveColor : masterInactiveColor}>
                        {c.status}
                      </span>
                    </td>
                    <td className={masterTableActionCell}>
                      <div className="flex justify-center gap-1">
                        <button onClick={() => handleEdit(c)} className={masterEditButton}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(c._id)} className="px-2 py-0.5 bg-red-500 text-white rounded text-[10px] font-bold uppercase">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinetMaster;